import React, { useState, useContext, useEffect } from 'react';
import { navigate } from 'gatsby';
import moment from 'moment';
import {
  Button,
  AuthActions,
  routes,
  breakpoints,
  AuthorContext,
  PageContext,
  GenerateTierListDescription,
  GenerateTierListKeywords,
  siteName,
  siteDescription,
  useLocalStorage,
  canUseLocalStorage,
  isMobile
} from 'gatsby-theme-apollo';
import { DragDropContext } from 'react-beautiful-dnd-short-press';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import ReactResizeDetector from 'react-resize-detector';
import uniqid from 'uniqid';
import { getUniqueTierListPermalink } from '../../services/tier-list-service';
import EditableText from '../text/editable-text';
import { defaultTiers, TierGrid, Tier, TierHeader, GridHeaderRowNameColumn, GridHeaderRowContentColumn } from './tier';
import Tray from './tray';
import { DraggableEntityThumbnail } from './draggable-entity-thumbnail';
import { EntityThumbnailSettings } from '../entities/entity-thumbnail';
import { validateString } from '../../util/validate';
import Color from '../../util/color';
import { Flex } from '../layout/flex';
import TierListHeader from './tier-list-header';
import SaveTierlistForm from './save-tier-list-form';
import FilterMenu from './filter-menu';
import { filterEntities } from '../../util/filter';
import getEntityStats from '../../util/stats';
import ShareBar from '../social/share-bar';
import { Comments } from '../social/comments';
import { gql, useMutation } from '@apollo/client';

const UPDATE_TIER_LIST = gql`
  mutation UpdateTierlist(
    $tierListID: ID!,
    $tierJSON: JSON,
    $title: String,
    $description: String,
    $lastPublish: DateTime!,
  ) {
    updateTierlist(
      input: {
        where: {id: $tierListID},
        data: {
          tierJSON: $tierJSON,
          title: $title,
          description: $description,
          lastPublish: $lastPublish
        }
      }
    ) {
      tierlist {
        permalink
      }
    }
  }
`;

const CREATE_TIER_LIST = gql`
  mutation createTierlist(
    $tierJSON: JSON!,
    $title: String!,
    $description: String,
    $lastPublish: DateTime!,
    $game: ID!,
    $author: ID!,
    $permalink: String!,
    $slug: String!,
  ) {
    createTierlist(
      input: {
        data: {
          tierJSON: $tierJSON,
          title: $title,
          description: $description,
          lastPublish: $lastPublish,
          game: $game,
          author: $author,
          permalink: $permalink
          slug: $slug
        }
      }
    ) {
      tierlist {
        id
        permalink
      }
    }
  }
`;

const TierListEditorRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const SearchRemainderIndicator = styled.div({
  height: `100px`,
  display: `flex`,
  padding: `0px 20px`,
  margin: `5px 20px`,
  color: `#777`,
  justifyContent: `center`,
  border: `1px solid #333`,
  alignItems: `center`
});

export const TierListControls = styled(Flex)({
  justifyContent: `center`,
  width: `100%`,
  padding: `5px`,
  [breakpoints.sm]: {
    padding: `5px 0px`
  }
});

const StatusText = styled.div(props => ({
  color: props.theme.primary,
  width: `100%`,
  textAlign: 'center',
  justifyContent: 'center',
}));

const tierListCacheKey = typeof window !== 'undefined' && window.location.pathname;

const clearTierlistLocalStorage = () => {
  if (canUseLocalStorage()) {
    localStorage.removeItem(`${tierListCacheKey}_tiers`);
    localStorage.removeItem(`${tierListCacheKey}_title`);
    localStorage.removeItem(`${tierListCacheKey}_shortDescription`);
  }
}

const TierListEditor = ({
  characters,
  tierData,
  loading,
  gameName,
  entityTypePlural,
  gameStats,
  tierListAuthor,
  gameID,
  tierListDescription,
  tierListTitle,
  tierListID,
  tierListLastPublish,
  isPublished,
  isBaked,
  comments
}) => {
  const entityDisplayLimit = 100;

  const { logEvent } = useContext(PageContext);

  const mobile = isMobile();

  // Determine which characters of the provided set need to be present in the tray.
  // This is used to filter the characters who are already in a tier out of the tray, when the user loads a tier list
  const getCharactersForTray = (chars, tiersToCheck) => {
    // We need to remove characters who are already in the loaded tier-list from the tray, so they aren't duplicated on-page
    const newChars = (chars && [...chars]) || [];
    if (tiersToCheck) {
      tiersToCheck.forEach(tier => {
        if (tier.characters) {
          tier.characters.forEach(characterInTier => {
            const characterFoundIndex = newChars.findIndex(characterForTray => {
              return characterForTray.id === characterInTier.id;
            });
            if (characterFoundIndex !== -1) {
              newChars.splice(characterFoundIndex, 1);
            }
          });
        }
      });
    }
    return newChars;
  };

  const { author: signedInAuthor } = useContext(AuthorContext);

  let filterStat;
  let filterableStats = [];

  if (gameStats) {
    gameStats.forEach(stat => {
      if (stat.visualType) {
        filterStat = stat;
      }
    });
    filterableStats = gameStats.filter(stat => {
      return stat && stat.filter && stat.filter === true;
    });
  }

  const signedInAuthorIsTierListAuthor =
    tierListAuthor && signedInAuthor && tierListAuthor.id === signedInAuthor.id;

  const [loaded, setLoaded] = useState(isBaked);
  const [showTray, setShowTray] = useState(false);
  const [tiers, setTiers] = useLocalStorage(
    `${tierListCacheKey}_tiers`,
    (tierData && JSON.parse(JSON.stringify(tierData))) || null
  );
  const [title, setTitle] = useLocalStorage(`${tierListCacheKey}_title`, tierListTitle);
  const [shortDescription, setShortDescription] = useLocalStorage(
    `${tierListCacheKey}_shortDescription`,
    tierListDescription || ''
  );

  const [charactersInTray, setCharactersInTray] = useState(getCharactersForTray(characters, tiers));
  const [searchText, setSearchText] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [devMode, setDevMode] = useState(false);
  const [titleCustomized, setTitleCustomized] = useState(false);
  const [descriptionCustomized, setDescriptionCustomized] = useState(false);
  const [lastReportedSearchText, setLastReportedSearchText] = useState('');

  const URL = typeof window !== 'undefined' && window.location.href;

  const [editorPageState, setEditorPageState] = useState({
    URL,
    entityTypePlural,
    isBaked,
    username: signedInAuthor && signedInAuthor.name,
    sessionStart: new Date(),
    sessionID: uniqid.time(),
    sessionEntityMoves: 0
  });

  const rulerRef = React.createRef();

  const [uiState, setuiState] = useState({
    width: 0,
    characterSize: EntityThumbnailSettings.small,
    highlights: {
      filterStat
    },
    filters: [],
    tierListURL: '',
    saved: false,
    saving: false,
    saveFailed: false,
    tierListTitle,
    currentlyDraggingDraggableId: 'none',
    editable: !isPublished
  });

  const logEditorEvent = (name, props = {}) => {
    const numberTiers = tiers && tiers.length;
    const numberEntitiesInTiers =
      tiers &&
      tiers.reduce((count, tier) => {
        return count + tier.characters.length;
      }, 0);

    if (searchText !== '' && searchText !== lastReportedSearchText) {
      logEvent('tier_list_action_entity_search', searchText);
      setLastReportedSearchText(searchText);
    }

    logEvent('tier_list_action', {
      name,
      ...editorPageState,
      numberTiers,
      numberEntitiesInTiers
    });

    logEvent(`tier_list_action_${name}`, {
      ...props,
      ...editorPageState,
      numberTiers,
      numberEntitiesInTiers
    });
  };

  const toggleFilter = (statName, statOption) => {
    const newFilterSet = [...uiState.filters];
    let indexOfStat = newFilterSet && newFilterSet.findIndex(filter => filter.name === statName);

    let filter = false;

    // Stat isn't yet in the set of filters
    if (indexOfStat === -1) {
      newFilterSet.push({
        name: statName,
        values: []
      });
      indexOfStat = newFilterSet.length - 1;
    }

    if (newFilterSet[indexOfStat].values.includes(statOption)) {
      const indexOfStatValue = newFilterSet[indexOfStat].values.indexOf(statOption);
      newFilterSet[indexOfStat].values.splice(indexOfStatValue, 1);
      filter = false;
    } else {
      newFilterSet[indexOfStat].values.push(statOption);
      filter = true;
    }

    if (newFilterSet[indexOfStat].values.length === 0) {
      newFilterSet.splice(indexOfStat);
    }

    logEditorEvent('filter_toggle', {
      statName,
      statOption,
      filter
    });

    setuiState({ ...uiState, filters: newFilterSet });
  };

  let pageTitle;
  let pageDescription;

  if (isPublished) {
    const authorAttribution =
      (tierListAuthor && tierListAuthor.name && ` by ${tierListAuthor.name}`) || '';

    const updatedFriendly = tierListLastPublish && moment(tierListLastPublish).format('MMM YYYY');

    pageTitle = `${tierListTitle}${authorAttribution} | ${gameName} Tier List | Updated ${updatedFriendly} | ${siteName}`;
    pageDescription = GenerateTierListDescription({
      tierList: tiers,
      ugcDescription: `${tierListDescription ? `${tierListDescription} | ` : ''}${siteDescription}`
    });
  } else {
    pageTitle = `${gameName} Tier List Maker | ${siteName}`;
    pageDescription = siteDescription;
  }

  const pageKeywords = GenerateTierListKeywords({ characters });

  const toggleTray = () => {
    const newShowTray = !showTray;
    logEditorEvent('tray_toggle', {
      newShowTray
    });
    setShowTray(newShowTray);
  };

  const addTier = () => {
    logEditorEvent('tier_add', { newNumberTiers: tiers && tiers.length + 1 });
    setTiers([
      ...tiers,
      {
        name: 'Tier',
        characters: []
      }
    ]);
  };

  const addCharacterToTier = (character, tierIndex, listIndex) => {
    if (typeof character === 'undefined') {
      return;
    }

    const oldTiersCopy = [...tiers];

    if (listIndex === -1) {
      oldTiersCopy[tierIndex].characters.push({ ...character });
    } else {
      oldTiersCopy[tierIndex].characters.splice(listIndex, 0, { ...character });
    }

    setTiers(oldTiersCopy);
  };

  const removeCharacterFromTier = (characterIn, tierIndex) => {
    if (typeof characterIn === 'undefined') {
      return;
    }

    const oldTiersCopy = [...tiers];
    const indexOfCharacter = oldTiersCopy[tierIndex].characters.findIndex(
      character => character.id === characterIn.id
    );

    if (indexOfCharacter !== -1) {
      oldTiersCopy[tierIndex].characters.splice(indexOfCharacter, 1);
    }

    setTiers(oldTiersCopy);
  };

  const removeCharacterFromTray = character => {
    const indexOfCharacter = charactersInTray.findIndex(c => c.id === character.id);
    const newCharactersInTray = [...charactersInTray];
    newCharactersInTray.splice(indexOfCharacter, 1);
    setCharactersInTray(newCharactersInTray);
  };

  const addCharactersToTray = (charactersToAdd, index = charactersInTray.length) => {
    let newCharactersInTray = [
      ...charactersInTray.slice(0, index),
      ...charactersToAdd,
      ...charactersInTray.slice(index)
    ];

    newCharactersInTray = newCharactersInTray.sort((characterA, characterB) => {
      if (characterA.name.toLowerCase() < characterB.name.toLowerCase()) {
        return -1;
      }
      return 1;
    });

    setCharactersInTray(newCharactersInTray);
  };

  const getCharacterById = id => {
    const foundChar = characters.find(character => character.id === id);
    return foundChar;
  };

  const removeTier = () => {
    const allowedRemoval = tiers && tiers.length - 1 >= 2;
    const newNumberTiers = allowedRemoval ? tiers.length - 1 : 2;

    logEditorEvent('tier_remove', { newNumberTiers, valid: allowedRemoval });

    if (allowedRemoval) {
      const oldTiersCopy = [...tiers];
      const deletedTier = oldTiersCopy.splice(-1, 1);

      const deletedTierCharacters = deletedTier[0].characters;
      addCharactersToTray(deletedTierCharacters);
      setTiers(oldTiersCopy);
    }
  };

  const clearTiers = () => {
    if (typeof window === 'undefined') return;
    logEditorEvent('tiers_empty');

    if (window.confirm('This will remove all characters and reset tier names. Are you sure?')) {
      setTiers([...defaultTiers()]);
      setCharactersInTray([...characters]);
      logEditorEvent('tiers_empty_confirm');
    } else {
      logEditorEvent('tiers_empty_cancel');
    }
  };

  const onResized = newWidth => {
    const newCharacterSize =
      newWidth > 600 ? EntityThumbnailSettings.large : EntityThumbnailSettings.small;

    setuiState({
      ...uiState,
      width: newWidth,
      characterSize: newCharacterSize
    });
  };

  // tierString example: "tier_1-row_2";
  const droppableStringToTierIndices = tierString => {
    try {
      const tierPortion = tierString.split('-')[0];
      const tierIndexStr = tierPortion.split('_')[1];
      const tierIndex = Number.parseInt(tierIndexStr, 10);

      const rowPortion = tierString.split('-')[1];
      const rowIndexStr = rowPortion.split('_')[1];
      const rowIndex = Number.parseInt(rowIndexStr, 10);

      return { tierIndex, rowIndex };
    } catch (e) {
      return -1;
    }
  };

  const renameTier = (tierIndex, newName) => {
    logEditorEvent('tier_rename_complete', {
      tierIndex,
      tierName: newName
    });

    const oldTiersCopy = [...tiers];
    oldTiersCopy[tierIndex].name = newName;
    setTiers(oldTiersCopy);
  };

  const resetTiersToPublished = () => {
    logEditorEvent('tiers_discard_changes');
    setTiers([...JSON.parse(JSON.stringify(tierData))]);
    setCharactersInTray(getCharactersForTray(characters, JSON.parse(JSON.stringify(tierData))));
    setTitle(tierListTitle);
    setShortDescription(tierListDescription);
  };

  const getEditorSaveState = () => {
    return {
      ...editorPageState,
      previouslyPublished: isPublished,
      title: tierListTitle,
      titleCustomized,
      description: shortDescription,
      descriptionCustomized,
      tierListID
    };
  };

  const titleMinLength = 3;
  const validateTitle = str => validateString(str, titleMinLength, /[^0-9a-zA-Z\s.']/g);
  const titleValidity = validateTitle(title);

  const trySaveTierList = () => {
    if (!titleValidity.valid) {
      return;
    }

    setuiState({ ...uiState, saving: true });
    const saveState = getEditorSaveState();

    logEditorEvent('save', {
      ...saveState
    });
 
    if (!tierListID) {

      getUniqueTierListPermalink({gameID, title})
        .then(result => {
          createTierlist({ variables: { 
            tierJSON: { dimensions: [{ name: 'Default', tiers: tiers }] },
            title: title,
            description: shortDescription,
            lastPublish: new Date(),
            game: gameID,
            author: signedInAuthor.id,
            permalink: result.permalinkResult,
            slug: result.slug
          } });
        })
        .catch(error => {
          console.error("trySaveTierList FAILED");
        });
    } else {
      updateTierlist({ variables: { 
        tierListID: tierListID,
        tierJSON: { dimensions: [{ name: 'Default', tiers: tiers }] },
        title: title,
        description: shortDescription,
        lastPublish: new Date(),
      } });
    }
  };

  const onDragStart = result => {
    const { draggableId } = result;
    setuiState({
      ...uiState,
      currentlyDraggingDraggableId: draggableId
    });
  };

  const onDragEnd = result => {
    const { destination, source, draggableId } = result;

    setuiState({
      ...uiState,
      currentlyDraggingDraggableId: ''
    });

    const sourceIsTray = source.droppableId.indexOf('tray') !== -1;
    const sourceIsTier = source.droppableId.indexOf('tier') !== -1;
    const { tierIndex: originTierIndex } = droppableStringToTierIndices(source.droppableId);

    // Log invalid move if there's no valid destination
    if (!destination) {
      logEditorEvent('entity_move', {
        originType: sourceIsTray ? 'tray' : 'tier',
        originColumnIndex: source && source.index,
        originTierIndex,
        destinationColumnIndex: destination && destination.index,
        validMove: false
      });
      return;
    }

    const destinationIsTier = destination.droppableId.indexOf('tier') !== -1;
    const destinationIsTray = destination.droppableId.indexOf('tray') !== -1;
    const sourceIsDestination =
      destination.droppableId === source.droppableId && destination.index === source.index;

    const charactersPerRow = Math.floor(
      (uiState.width - uiState.characterSize.width / 2) /
        (uiState.characterSize.width + uiState.characterSize.margin * 2)
    );

    const character = getCharacterById(draggableId);

    const { tierIndex: destinationTierIndex, rowIndex } = droppableStringToTierIndices(
      destination.droppableId
    );

    logEditorEvent('entity_move', {
      originType: sourceIsTray ? 'tray' : 'tier',
      originColumnIndex: source && source.index,
      originTierIndex,
      destinationType: destinationIsTray ? 'tray' : 'tier',
      destinationTierIndex,
      destinationColumnIndex: destination && destination.index,
      validMove: !sourceIsDestination && !(sourceIsTray && destinationIsTray)
    });

    if (!(sourceIsTray && destinationIsTray) && !sourceIsDestination) {
      setEditorPageState({
        ...editorPageState,
        sessionEntityMoves: editorPageState.sessionEntityMoves + 1
      });
    } else {
      return;
    }

    if (sourceIsTier) {
      removeCharacterFromTier(character, originTierIndex);
    } else if (sourceIsTray) {
      removeCharacterFromTray(character);
    }

    if (destinationIsTier) {
      const insertIndex = rowIndex * charactersPerRow + destination.index;
      addCharacterToTier(character, destinationTierIndex, insertIndex);
    } else if (destinationIsTray) {
      addCharactersToTray([character], destination.index);
    }
  };

  useEffect(() => {
    if (!isBaked) {
      if (!loading && !loaded) {
        let loadedTierData = JSON.parse(JSON.stringify(tierData));
        setTiers(loadedTierData);

        const charactersForTray = getCharactersForTray([...characters], loadedTierData);
        setCharactersInTray(charactersForTray);

        if (tierListTitle) {
          setTitle(tierListTitle);
        }

        if (tierListDescription) {
          setShortDescription(tierListDescription);
        }

        if (signedInAuthorIsTierListAuthor || !isPublished) {
          setShowTray(true);
        }
        setLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loaded, tierListDescription, tierListTitle, tierData]);

  const [tiersEditsMade, setTiersEditsMade] = useState(false);
  const [textEditsMade, setTextEditsMade] = useState(false);

  useEffect(() => {
    setTiersEditsMade(JSON.stringify(tiers) !== JSON.stringify(tierData));
  }, [tierData, tiers])

  useEffect(() => {
    setTextEditsMade(titleCustomized || descriptionCustomized);
  }, [titleCustomized, descriptionCustomized])

  const UpdateTrayMode = () => {
    setShowTray(signedInAuthorIsTierListAuthor || !isPublished);
    if (tierListAuthor && !signedInAuthor) {
      setShowTray(false);
    }
  };

  // Execute on mount
  useEffect(() => {
    if (signedInAuthor) {
      UpdateTrayMode();
      setEditorPageState({
        ...editorPageState,
        username: signedInAuthor && signedInAuthor.name
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedInAuthor]);

  useEffect(() => {
    UpdateTrayMode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let rowStart = 2; // Start after the "header row"
  let rowEnd = 0; // Gets initialized to the correct row value on first map func invocation

  const charactersPerRow = Math.floor(
    (uiState.width - uiState.characterSize.width / 2) /
      (uiState.characterSize.width + uiState.characterSize.margin * 2)
  );

  const filteredEntities = filterEntities(charactersInTray, searchText, uiState.filters);
  const firstEntity = filteredEntities && filteredEntities.length > 0 && filteredEntities[0];

  const showSearchRemainder = filteredEntities && filteredEntities.length > entityDisplayLimit;

  const [updateTierlist, { loading: mutatedTierListLoading, error: mutatedTierListError, data: mutatedTierListData }] = useMutation(UPDATE_TIER_LIST);
  const [createTierlist, { loading: newlyCreatedTierListLoading, error: newlyCreatedTierListError, data: newlyCreatedTierListData }] = useMutation(CREATE_TIER_LIST);

  useEffect(() => {
    if (!mutatedTierListLoading && mutatedTierListData) {
      clearTierlistLocalStorage();
      const savedTierListURL = routes.SavedTierList(gameName, mutatedTierListData.updateTierlist.tierlist.permalink);
      navigate(savedTierListURL);
    }
  }, [mutatedTierListData, mutatedTierListLoading, gameName]);

  useEffect(() => {
    if (!newlyCreatedTierListLoading && newlyCreatedTierListData) {
      clearTierlistLocalStorage();
      const savedTierListURL = routes.SavedTierList(gameName, newlyCreatedTierListData.createTierlist.tierlist.permalink);
      navigate(savedTierListURL);
    }
  }, [newlyCreatedTierListData, newlyCreatedTierListLoading, gameName]);

  return (
    <>
      {!loading && tiers !== undefined && (
        <>
          <Helmet>
            <title>{pageTitle}</title>
            <meta property="og:title" content={pageTitle} />
            <meta name="description" content={pageDescription} />
            <meta property="og:description" content={pageDescription} />
            {tierListLastPublish && (
              <meta property="og:updated_time" content={tierListLastPublish} />
            )}
            <meta name="keywords" content={pageKeywords} />
            <meta name="twitter:site" content="@gamerhubgg" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
          </Helmet>
          <SaveTierlistForm
            isPublished={isPublished}
            logEditorEvent={logEditorEvent}
            titleValidity={titleValidity}
            tierListTitle={title}
            tierListDescription={shortDescription}
            onTitleEditFinished={newValue => {
              setTitle(newValue);
              setTitleCustomized(true);
            }}
            onDescriptionEditFinished={newValue => {
              setShortDescription(newValue);
              setDescriptionCustomized(true);
            }}
            url={uiState.tierListURL}
            saving={uiState.saving}
            saveFailed={uiState.saveFailed}
          />
          {isPublished && (
            <TierListHeader
              title={title}
              description={shortDescription}
              author={tierListAuthor}
              lastPublish={tierListLastPublish}
            />
          )}

          {isPublished && (
            <Flex style={{ width: '100%', flexWrap: `wrap` }}>
              <ShareBar logEditorEvent={logEditorEvent} />
            </Flex>
          )}

          <Flex
            justifyContent="space-between"
            wrap="wrap"
            style={{ marginBottom: `5px`}}
          >
            <Flex justifyContent="flex-end">
              {mutatedTierListLoading && <p>Saving...</p>}
              {newlyCreatedTierListLoading && <p>Creating...</p>}
              {mutatedTierListError && <p>Error saving tier list :( Please try again</p>}
              {newlyCreatedTierListError && <p>Error creating tier list :( Please try again</p>}
              {(!isPublished || signedInAuthorIsTierListAuthor) && (
                <AuthActions
                  cta="💾 Save"
                  hideSignOut
                  saveOnSignIn
                  intent="save_tier_list"
                  uiSource="editor_save"
                  onSignIn={() => {
                    trySaveTierList();
                  }}
                >
                  {signedInAuthor && (
                    <>
                      {isPublished && (tiersEditsMade || textEditsMade) && (
                        <Button onClick={() => resetTiersToPublished()}>{'Discard Changes'}</Button>
                      )}
                      {((tiersEditsMade || textEditsMade) && titleValidity.valid) ? 
                        <Button
                          primary
                          onClick={() => {
                            const saveState = getEditorSaveState();
                            logEditorEvent('save', {
                              ...saveState,
                              interactionLocation: 'editor'
                            });
                            trySaveTierList();
                          }}
                        >
                          {isPublished ? '💾 Update' : '💾 Save'}
                        </Button>
                      :
                        <Button
                        >
                          {isPublished ? '💾 Update' : '💾 Save'}
                        </Button>
                      }
                    </>
                  )}
                </AuthActions>
              )}

              {isPublished && !tiersEditsMade && signedInAuthorIsTierListAuthor && (
                <StatusText>{`Your tiers are published!`}</StatusText>
              )}
            </Flex>
          </Flex>

          <TierListEditorRoot>
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <TierGrid>
                <GridHeaderRowNameColumn />
                <GridHeaderRowContentColumn ref={rulerRef}>
                  <ReactResizeDetector
                    handleWidth
                    refreshRate={10}
                    onResize={e => {
                      const { activeElement } = document;
                      if (
                        activeElement &&
                        activeElement.dataset &&
                        activeElement.dataset.skipResize
                      ) {
                        return;
                      }
                      onResized(e);
                    }}
                  />
                </GridHeaderRowContentColumn>
                {tiers.map((tier, index) => {
                  const rowsNeeded = Math.max(
                    1,
                    Math.floor(tier.characters.length / charactersPerRow) + 1
                  );
                  const color = Color({
                    value: index,
                    maxValue: tiers.length - 1
                  });

                  // rowStart & rowEnd defines where TierHeaders need to begin and end in the grid.
                  rowEnd = rowStart + rowsNeeded;

                  const result = (
                    <React.Fragment key={index}>
                      <TierHeader
                        bgColor={color}
                        rowStart={rowStart}
                        rowEnd={rowEnd}
                      >
                        <EditableText
                          textValue={tier.name}
                          maxCharacters={100}
                          textAlign="center"
                          hideBorder
                          inputType="content-editable"
                          onFocus={() => {
                            logEditorEvent('tier_rename_start', {
                              ...editorPageState,
                              tierIndex: index
                            });
                          }}
                          onBlur={e => {
                            const ruler = rulerRef && rulerRef.current;
                            if (ruler) {
                              const { width } = ruler.getBoundingClientRect();
                              onResized(width);
                            }
                            renameTier(index, e.target && e.target.innerHTML);
                          }}
                          onClicked={() => {
                            if (mobile) {
                              setShowTray(false);
                            }
                          }}
                        />
                      </TierHeader>
                      <Tier
                        tierIndex={index}
                        entityTypePlural={entityTypePlural}
                        gameName={gameName}
                        gameStats={filterableStats}
                        characters={tier.characters}
                        characterSet={characters}
                        charactersPerRow={charactersPerRow}
                        rowsNeeded={rowsNeeded}
                        currentlyDraggingDraggableId={uiState.currentlyDraggingDraggableId}
                        highlights={uiState.highlights}
                        searchText={searchText}
                        filters={uiState.filters}
                        showDropPreviewRows={!isPublished}
                        color={color}
                        size={uiState.characterSize}
                        selected={selectedTier === index}
                        logEditorEvent={logEditorEvent}
                        onSelected={() => {
                          if (devMode) {
                            setSelectedTier(index);
                          }
                        }}
                      />
                    </React.Fragment>
                  );

                  // The next row's header needs to know how far away to begin
                  rowStart += rowsNeeded;

                  return result;
                })}
              </TierGrid>

              <TierListControls>
                <Button onClick={() => clearTiers()}>
                  <span role="img" aria-label="Empty">
                    🔄
                  </span>
                  Empty
                </Button>
                <Button onClick={() => removeTier()}>
                  <span role="img" aria-label="Minus">
                    ➖
                  </span>
                  Tier
                </Button>
                <Button onClick={() => addTier()}>
                  <span role="img" aria-label="Plus">
                    ➕
                  </span>
                  Tier
                </Button>
              </TierListControls>

              {filterableStats && filterableStats.length >= 1 && (
                <FilterMenu
                  stats={filterableStats}
                  filterStat={filterStat}
                  filters={uiState.filters}
                  onFilterToggled={toggleFilter}
                />
              )}

              <Tray
                show={showTray}
                onToggleTray={toggleTray}
                editable={true}
                searchText={searchText}
                devMode={devMode}
                onSearch={newValue => {
                  setSearchText(newValue);
                  setEditorPageState({
                    ...editorPageState,
                    searchText: newValue
                  });
                  if (newValue === 'boppy') {
                    setDevMode(true);
                    setSelectedTier(0);
                  }
                }}
                onKeyPress={keyCode => {
                  if (devMode) {
                    if (keyCode === 17) {
                      if (firstEntity) {
                        addCharacterToTier(firstEntity, selectedTier);
                        removeCharacterFromTray(firstEntity);
                      }
                    }
                    if (keyCode === 38) {
                      if (selectedTier > 0) {
                        setSelectedTier(selectedTier - 1);
                      }
                    }
                    if (keyCode === 40) {
                      if (selectedTier < tiers.length - 1) {
                        setSelectedTier(selectedTier + 1);
                      }
                    }
                  }
                }}
                logEditorEvent={logEditorEvent}
              >
                {charactersInTray &&
                  filteredEntities
                    .slice(0, entityDisplayLimit)
                    .map(
                      (entity, index) =>
                        entity && (
                          <DraggableEntityThumbnail
                            key={`${entity.name}-char-thumb`}
                            index={index}
                            gameStats={filterableStats}
                            character={entity}
                            stats={getEntityStats({ entity, entitiesWithStats: characters })}
                            highlights={uiState.highlights}
                            currentlyDraggingDraggableId={uiState.currentlyDraggingDraggableId}
                            size={uiState.characterSize}
                          />
                        )
                    )}
                {showSearchRemainder && (
                  <SearchRemainderIndicator>
                    To improve Performance, we don't show all {entityTypePlural} at once. Search for
                    the one you're looking for, and it'll appear!
                  </SearchRemainderIndicator>
                )}
              </Tray>
            </DragDropContext>
          </TierListEditorRoot>

          {isPublished && 
            <Flex style={{ width: '100%', flexWrap: `wrap` }}>
              <ShareBar logEditorEvent={logEditorEvent} location="bottom" />
            </Flex>
          }

          {isPublished && (
            <Comments
              contentID={tierListID}
              comments={comments}
              contentAuthorID={tierListAuthor && tierListAuthor.id}
            />
          )}
          
          <div style={{ marginBottom: `30px` }} />
        </>
      )}
    </>
  );
};

export default TierListEditor;
